<template>
  <div class="relative" :class="$attrs.class">
    <div class="absolute inset-y-0 left-0 flex max-h-12 items-center pl-4">
      <slot name="prepend" />
    </div>
    <label class="block">
      <span class="sr-only">{{ label }}</span>
      <input
        ref="inputRef"
        :key="typeInput"
        v-ui-test-name="'uiInput'"
        v-mask="mask"
        v-bind="{ ...$attrs, class: '' }"
        :value="model"
        :class="[
          'focus-ringable flex w-full appearance-none rounded-lg p-4 text-sm leading-4 outline-none ring-1 ring-neutral-100 transition duration-100 ease-in',
          'placeholder:text-neutral-500',
          'disabled:pointer-events-none disabled:bg-white disabled:opacity-30 disabled:ring-1 ',
          'xs:text-[1.143rem]',
          {
            'focus-ringable--primary': !isValidationError && !invalid,
            '!ring-2 !ring-error-500': isValidationError || invalid,
            '!ring-2 !ring-success-500': valid,
            'pl-14': $slots.prepend,
            'pr-14 xs:pr-12': $slots.append || isPasswordToggle,
            '!rounded-l-none': phone,
            'py-3': compact,
          },
          textClass,
        ]"
        :type="typeInput"
        :readonly="autocompleteOff ? isReadonly : ($attrs.readonly as boolean)"
        @focus="setInputTypeable"
        @input="onInput"
      />
    </label>
    <div
      class="absolute inset-y-0 right-0 flex max-h-12 items-center pr-4 xs:max-h-full"
    >
      <slot name="append" />

      <template v-if="isPasswordToggle && model.length">
        <button type="button" @click="togglePasswordVisibility">
          <UiIcon
            :name="isPasswordVisible ? 'outline/eye-off' : 'outline/eye'"
            class="size-4 text-neutral-500"
          />
        </button>
      </template>
    </div>
    <span
      v-if="!phone && !$attrs.disabled && isValidationError"
      v-ui-test-name="'uiTextInputError'"
      class="mt-1 flex text-xs text-error-500"
    >
      {{ errors[0] }}
    </span>
    <span
      v-if="!isValidationError && valid && successMessage"
      v-ui-test-name="'uiTextInputSuccess'"
      class="mt-1 flex text-xs text-success-500"
    >
      {{ successMessage }}
    </span>
  </div>
</template>

<script lang="ts" setup>
/// <reference lib="dom" />

import type { PropType } from 'vue'

const props = defineProps({
  modelValue: { type: String, required: true },
  errors: { type: Array as PropType<string[]>, default: () => [] },
  mask: { type: String, default: '' },
  label: { type: String, default: '' },
  isPasswordToggle: { type: Boolean },
  autocompleteOff: { type: Boolean },
  focus: {
    type: Boolean,
    default: false,
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  valid: {
    type: Boolean,
    default: false,
  },
  successMessage: {
    type: String,
    default: '',
  },
  phone: {
    type: Boolean,
    required: false,
    default: false,
  },
  textClass: {
    type: String,
    default: '',
  },
  noTrim: {
    type: Boolean,
    required: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const model = ref(props.modelValue || '')
const inputRef = ref<HTMLElement | null>(null)
const isReadonly = ref(true)

/**
 * делаем поле НЕ readonly при фокусе для случаев когда не нужно автозаполнение
 */
const setInputTypeable = () => {
  isReadonly.value = false
}

/**
 * Определяет, есть ли ошибка валидации
 */
const isValidationError = computed(
  () => !!props.errors && props.errors.length > 0,
)

/**
 * Определяет тип инпута в зависимости от переданных пропсов
 */
const isPasswordVisible = ref(false)
const attrs = useAttrs()

const typeInput = computed(() => {
  if (props.isPasswordToggle)
    return isPasswordVisible.value ? 'text' : 'password'

  return attrs.type as string
})

const togglePasswordVisibility = () => {
  isPasswordVisible.value = !isPasswordVisible.value
}

/**
 * Обрабатывает пользовательский ввод, генерирует событие 'input'
 */
watch(model, (newValue) => {
  emit('update:modelValue', props.noTrim ? newValue : newValue.trim())
})

/**
 * Обрабатывает событие ввода
 * выделено отдельно специально для реактивности v-model в браузере на андроиде
 * https://stackoverflow.com/a/71844469
 * @param {Event} event - событие ввода
 */
const onInput = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}

/**
 * Если передали focus = true, делаем фокус на инпуте (один раз)
 */
watch(
  () => props.focus,
  (newValue: boolean, oldValue: boolean) => {
    if (!oldValue && newValue) {
      inputRef.value?.focus()
    }
  },
)

/**
 * Обновляем model при обновлении props.value
 * На случай, если обновили props.value снаружи, например, очистили поле промокода
 */
watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (model.value !== newValue && newValue !== oldValue) {
      model.value = newValue
    }
  },
)
</script>

<script lang="ts">
export default defineComponent({
  inheritAttrs: false,
})
</script>
